import React, {FC, useState} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {useMonthEndFeatureAccess} from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'

const API_URL = process.env.REACT_APP_API_URL

type Module1ReportNameModalProps = {
  isOpen: boolean
  onClose: () => void
  companyGroupId?: string
  onSaved: () => void
}

const Module1ReportNameModal: FC<Module1ReportNameModalProps> = ({
  isOpen,
  onClose,
  companyGroupId,
  onSaved,
}) => {
  const {auth, currentUser} = useAuth()
  const {canCreate, isGlobalScope} = useMonthEndFeatureAccess('module1-report')

  const [reportName, setReportName] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const resetForm = () => {
    setReportName('')
    setIsActive(true)
    setError('')
  }

  const handleClose = () => {
    if (saving) return
    resetForm()
    onClose()
  }

  const handleSave = async () => {
    if (!reportName.trim()) {
      setError('Please enter a report name')
      return
    }
    if (isGlobalScope && !companyGroupId) {
      setError('Please select a company first')
      return
    }
    if (!auth?.auth?.jwt) {
      setError('Authentication token missing')
      return
    }

    setSaving(true)
    try {
      const payload: any = {
        name: reportName.trim(),
        isActive,
      }

      if (isGlobalScope) {
        payload.companyGroupAdminId = Number(companyGroupId)
      } else if (currentUser?.companyGroupAdminId != null) {
        payload.companyGroupAdminId = Number(currentUser.companyGroupAdminId)
      } else if (currentUser?.companyGroupAdmin?.id != null) {
        payload.companyGroupAdminId = Number(currentUser.companyGroupAdmin.id)
      }

      const response = await fetch(`${API_URL}/qhse/module1-report-names`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.auth.jwt}`,
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err?.message || 'Failed to save report name')
      }

      toast.success('Report name added successfully')
      onSaved()
      resetForm()
      onClose()
    } catch (err: any) {
      console.error('Failed to save module 1 report name:', err)
      setError('Failed to save report name')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null
  if (!canCreate) {
    return (
      <div className='modal fade show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Add Report Name</h5>
              <button type='button' className='btn-close' onClick={handleClose} disabled={saving} />
            </div>
            <div className='modal-body'>
              <QhseAccessDenied message='Access denied — you do not have permission to create Month End Report names.' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='modal fade show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Add Report Name</h5>
            <button type='button' className='btn-close' onClick={handleClose} disabled={saving} />
          </div>

          <div className='modal-body'>
            {error && <div className='alert alert-danger py-2'>{error}</div>}

            <div className='mb-4'>
              <label className='form-label required fw-semibold fs-6 mb-2 text-gray-700'>Report Name</label>
              <input
                type='text'
                className='form-control form-control-solid'
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder='Enter report name'
                disabled={saving}
              />
            </div>

            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={saving}
              />
              <label className='form-check-label fw-semibold text-gray-700'>Active</label>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={handleClose} disabled={saving}>
              Cancel
            </button>
            <button type='button' className='btn btn-primary' onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' />Saving...
                </>
              ) : (
                <>
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-4 me-2' />
                  Save Report Name
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Module1ReportNameModal
